import { createCategoryBodySchema, type CreateCategoryBody } from '@klopotun/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FormEvent } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button, ColorField, EmojiField, TextField } from '../../../../shared/ui/index.js';
import { CATEGORY_EMOJIS } from '../../../../shared/config/category-emojis.js';
import { toCategoryBodyDraft } from '../../lib/category-form-adapter.js';
import { categoryFormSchema } from '../../lib/category-form-schema.js';
import type { CategoryFormValues } from '../../model/category-form-values.js';
import styles from './category-form.module.css';

type CategoryFormProps = {
  initialValues: CategoryFormValues;
  onSubmit: (body: CreateCategoryBody) => void;
  onCancel: () => void;
  busy: boolean;
};

export const CategoryForm = ({ initialValues, onSubmit, onCancel, busy }: CategoryFormProps) => {
  const { control, handleSubmit } = useForm<CategoryFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(categoryFormSchema),
    mode: 'onTouched',
  });

  const save = handleSubmit((values) => {
    onSubmit(createCategoryBodySchema.parse(toCategoryBodyDraft(values)));
  });

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    void save(event);
  };

  return (
    <form className={styles.form} onSubmit={handleFormSubmit} noValidate>
      <div className={styles.fields}>
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <TextField
              label="Назва"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              placeholder="Підписки"
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="emoji"
          render={({ field, fieldState }) => (
            <EmojiField
              label="Емодзі"
              name={field.name}
              value={field.value}
              presets={CATEGORY_EMOJIS}
              onChange={field.onChange}
              onBlur={field.onBlur}
              hint="Не обов'язково"
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="color"
          render={({ field, fieldState }) => (
            <ColorField
              label="Колір"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              hint="Не обов'язково"
              error={fieldState.error?.message}
            />
          )}
        />
      </div>

      <div className={styles.actions}>
        <Button type="submit" busy={busy}>
          Зберегти
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Скасувати
        </Button>
      </div>
    </form>
  );
};
